import { Component, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { CheckoutActions } from '@app/pages/checkout-app/checkout-store/actions';
import { CheckoutReducer } from '@app/pages/checkout-app/checkout-store/reducers';
import { RulesDynamicFormService } from '@app/sharedModule/components/rules-dynamic-form/rules-dynamic-form.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Actions } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { ExtensionComponentType, ICheckoutExtensions } from '@pdqdev/pdq-shared/dist/interfaces/ICheckoutExtensions';
import { generateRandomString } from '@utils/string-utils';
import { ViewMode } from '../../../../models';
import { getExtensionRules } from '../../../../shared/all-extension-rules';
import { ShopService } from '@services/shops/shop.service';
import { ExtensionsService } from '@app/pages/checkout-app/services/extensions/extensions.service';
import { HistoricalPaymentService } from './historical-payment-info.service';

@Component({
  selector: 'hooxpay',
  templateUrl: './hoox-pay.component.html',
  styleUrls: ['./hoox-pay.component.scss'],
  standalone: false,
  providers: [HistoricalPaymentService],
})
@UntilDestroy()
export class HooxPayComponent implements OnInit, OnDestroy {
  loading$ = this.store.select(CheckoutReducer.selectIsLoading);
  loadedExtension$ = this.store.select(CheckoutReducer.selectActiveExtension);
  loadedExtension: ICheckoutExtensions;
  formGroup: UntypedFormGroup;
  restrictionsRules = getExtensionRules();
  entities = this.restrictionsRules.map((rule) => rule.entity);
  shopCurrencySymbol: string;
  private isLoading = false;

  // Getters to expose service state for template binding
  get isHistoricalPaymentDataFetchingCompleted(): boolean {
    return this.historicalPaymentService.isCompleted;
  }

  get isHistoricalPaymentDataFetchingInProgress(): boolean {
    return this.historicalPaymentService.isInProgress;
  }

  get historicalPaymentDataSubtext(): string | null {
    return this.historicalPaymentService.subtext;
  }

  get historicalPaymentDataTooltip(): string | null {
    return this.historicalPaymentService.tooltip;
  }

  get isHistoricalPaymentDataToggleLocked(): boolean {
    const isToggleOn = this.formGroup?.get('useHistoricalData')?.value === true;
    return this.historicalPaymentService.isToggleLocked || (this.isLoading && isToggleOn);
  }

  get isHistoricalPaymentDataToggleDisabled(): boolean {
    const isToggleOn = this.formGroup?.get('useHistoricalData')?.value === true;
    return this.isLoading && !isToggleOn;
  }

  constructor(
    private store: Store<CheckoutReducer.State>,
    private rulesDynamicFormService: RulesDynamicFormService,
    public shopService: ShopService,
    private extensionsService: ExtensionsService,
    private actions$: Actions,
    private historicalPaymentService: HistoricalPaymentService,
  ) {}

  ngOnInit() {
    this.extensionsService.setCurrentExtensionType(ExtensionComponentType.HOOXPAY);
    this.extensionsService.save$.pipe(untilDestroyed(this)).subscribe(() => {
      this.handleSave();
    });

    this.shopCurrencySymbol = this.shopService.shopCurrencySymbol;
    this.initForm();
    this.initSubscriptions();
    this.historicalPaymentService.load((data) => {
      if (data != null) {
        this.formGroup.get('useHistoricalData').setValue(true, { emitEvent: false });
      }
    });
  }

  handleSave() {
    const formValue = this.formGroup.value;
    const usageToken = formValue.usageToken;
    delete formValue.usageToken;
    const template = this.prepareFormValues(formValue);
    const payload: Partial<ICheckoutExtensions> = {
      template,
      usageToken,
      type: ExtensionComponentType.HOOXPAY,
    };

    if (this.loadedExtension?.id) {
      const id = this.loadedExtension.id;
      this.store.dispatch(CheckoutActions.updateExtension({ id, extension: { ...payload, id } }));
    } else {
      this.store.dispatch(CheckoutActions.saveExtension({ extension: payload }));
    }
  }

  private initSubscriptions() {
    this.initLoadedExtensionSubscription();
    this.initValueChangesSubscription();
    this.initLoadingSubscription();
  }

  private initLoadingSubscription() {
    this.loading$.pipe(untilDestroyed(this)).subscribe((loading) => {
      this.isLoading = loading;
    });
  }

  private initLoadedExtensionSubscription() {
    this.loadedExtension$.pipe(untilDestroyed(this)).subscribe((extension: ICheckoutExtensions) => {
      this.loadedExtension = extension;
      if (extension) {
        this.initForm(extension);
      }
    });
  }

  private initValueChangesSubscription() {
    this.formGroup.valueChanges.pipe(untilDestroyed(this)).subscribe((values) => {
      this.dispatchExampleViewValues(values);
    });
  }

  private prepareFormValues(values) {
    const template = {
      title: values.title,
      internalName: values.internalName,
      restrictionsRules: values.restrictionsRules,
      bannerClosed: values.bannerClosed,
      useHistoricalData: values.useHistoricalData,
    };

    return template;
  }

  private dispatchExampleViewValues(values) {
    if (!values) {
      return;
    }

    const template = this.prepareFormValues(values);
    const mode = this.loadedExtension ? ViewMode.EDIT : ViewMode.NEW;
    const data = {
      [ExtensionComponentType.HOOXPAY]: {
        mode,
        template,
      },
    };
    this.store.dispatch(CheckoutActions.liveExampleTemplateView({ data }));
  }

  private initForm(extension?: ICheckoutExtensions) {
    const templateValue = extension?.template ?? {};

    this.formGroup = new UntypedFormGroup({
      usageToken: new UntypedFormControl(extension?.usageToken || 'pdqcashback_' + generateRandomString(12)),
      title: new UntypedFormControl(templateValue?.title ?? 'PDQ Cash back', Validators.required),
      internalName: new UntypedFormControl(templateValue?.internalName ?? 'PDQ Cash back'),
      restrictionsRules: this.rulesDynamicFormService.initRulesFormArray(
        templateValue?.restrictionsRules ?? [],
        getExtensionRules(),
      ),
      bannerClosed: new UntypedFormControl(templateValue?.bannerClosed ?? false),
      useHistoricalData: new UntypedFormControl(this.historicalPaymentService.data != null),
    });

    this.dispatchExampleViewValues(this.formGroup.value);

    this.formGroup.statusChanges.pipe(untilDestroyed(this)).subscribe(() => {
      this.extensionsService.setFormValid(this.formGroup.valid);
    });
    this.extensionsService.setFormValid(this.formGroup.valid);
  }

  onCloseBanner() {
    this.formGroup.get('bannerClosed').setValue(true);
  }

  ngOnDestroy() {
    this.historicalPaymentService.destroy();
  }
}
